import * as amplitude from "@amplitude/analytics-browser";
import { useApolloClient } from "@apollo/client";
import { DownloadIcon } from "@chakra-ui/icons";
import { Button, Icon, useDisclosure } from "@chakra-ui/react";
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { FormTabsTypes, SearchAndFilterTypes } from "src/constants";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { Selection } from "src/hooks/useMultiselectState";
import { useOrganization } from "src/hooks/useOrganization";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import useUser from "src/hooks/useUser";
import { triggerDownloadFromUrl } from "src/services/dataTransfer";
import * as FormTemplate from "src/services/formTemplate";
import { Question } from "src/services/formTemplate";
import { Status } from "src/types/authData";
import * as GQL from "src/types/graphql";
import { ExportRequest, SelectionType } from "../../dataServices/exports/types";
import { useExportManagementAPI } from "../../dataServices/exports/useExportManagementAPI";
import {
  getAllDefaultNonQuestionColumnKeys,
  getExportColumnKeysFromSearchParams,
  isQuestionColumnKey,
} from "../export/columnUtils";
import { GET_TAG_GROUPS_BY_ENROLLMENT_PERIOD } from "../graphql/queries";
import {
  FormIdListResult,
  SchoolFormKeyRecord,
  SchoolFormKeyRecordFactory,
} from "../types";
import { BulkExportDialog } from "./BulkExportDialog";

interface BulkExportButtonProps {
  formTemplate?: GQL.FormTemplateFragment;
  onFetchAllIds: () => Promise<FormIdListResult>;
  selection: Selection<SchoolFormKeyRecord, unknown>;
  dropoffFormTemplateId?: uuid;
}

export const BulkExportButton: React.FC<BulkExportButtonProps> = ({
  formTemplate,
  onFetchAllIds,
  selection,
  dropoffFormTemplateId,
}) => {
  const toast = useAvelaToast();
  const organization = useOrganization();
  const user = useUser();
  const api = useExportManagementAPI();
  const flags = useFlags([
    "current-applying-schools",
    "saved-view-custom-columns",
  ]);
  const savedViewCustomColumnsEnabled =
    flags["saved-view-custom-columns"].enabled;

  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { isSchoolAdmin, allSchoolIds } = useSchoolAdmin();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    isOpen: submittedIsOpen,
    onClose: submittedOnClose,
    onOpen: submittedOnOpen,
  } = useDisclosure({
    onClose: () => {
      // remove exportSubmittedText and scheduled from state
      const { exportSubmittedText, scheduled, ...otherState } =
        location.state ?? {};
      navigate(".", {
        state: otherState,
        replace: true,
      });
    },
  });

  // Use location.state to keep track of export customization form submission state
  // The form is on a different page from this button component
  const { exportSubmittedText, scheduled } = location.state ?? {};
  useEffect(() => {
    if (exportSubmittedText) {
      submittedOnOpen();
    }
  }, [exportSubmittedText, navigate, searchParams, submittedOnOpen]);

  const getErrorHandler = (toastId: string) => {
    return (error: unknown) => {
      const errorName =
        error instanceof Error
          ? error.constructor.name
          : typeof error === "string"
          ? "String"
          : "Unknown";
      amplitude.track("Failed bulk export", {
        count: selection.size,
        error_name: errorName,
      });
      toast.update(toastId, {
        title: "Error exporting forms",
        description: "Please try again later or contact our support team.",
        status: "error",
        duration: null,
        isClosable: true,
      });
      console.error(JSON.stringify(error));
    };
  };

  const isImportTab =
    searchParams.get(SearchAndFilterTypes.Tab) ===
    FormTabsTypes.FormListImports;
  // For import tab, the ids are only in the frontend, so if the selection is exclusive,
  // we need to materialize it and send it as an inclusive selection to the backend.
  const requiresInclusiveMaterialization =
    isImportTab && !selection.isInclusive;

  const schoolRankingSection = formTemplate?.sections.find(
    (section) =>
      section?.type === GQL.form_template_section_type_enum.SchoolRankingSection
  );
  const rankingEnabled =
    schoolRankingSection?.schools_ranking_section?.ranking_enabled ?? false;

  // Use getQuestionIdsAndLabelsInOrder with school filtering for school admins
  // to ensure school-specific questions are correctly filtered
  const questionIdsAndLabelsInOrder = Question.getQuestionIdsAndLabelsInOrder(
    formTemplate ? FormTemplate.fromGQL(formTemplate).sections : undefined,
    isSchoolAdmin
      ? {
          filterBy: "school",
          schoolIdSet: new Set(allSchoolIds),
        }
      : { filterBy: "none" }
  );
  const questionIdsInOrder = questionIdsAndLabelsInOrder.map((q) => q.id);

  const getSelectionObject = useCallback(async () => {
    if (requiresInclusiveMaterialization) {
      return {
        selectionType: SelectionType.Inclusive,
        ids: (
          await selection.materializeKeys(async () =>
            (await onFetchAllIds()).items.map(SchoolFormKeyRecordFactory)
          )
        ).map((key) => key.toObject()),
      };
    }
    return {
      selectionType: selection.isInclusive
        ? SelectionType.Inclusive
        : SelectionType.Exclusive,
      ids: selection.keysIncludedOrExcluded,
    };
  }, [requiresInclusiveMaterialization, selection, onFetchAllIds]);

  const handleBulkExport = async () => {
    const toastId = `bulk-export-${Date.now()}`;
    const errorHandler = getErrorHandler(toastId);
    try {
      const tagGroupsData = await apolloClient.query<
        GQL.GetTagGroupsByEnrollmentPeriod,
        GQL.GetTagGroupsByEnrollmentPeriodVariables
      >({
        query: GET_TAG_GROUPS_BY_ENROLLMENT_PERIOD,
        variables: {
          enrollment_period_id: formTemplate?.enrollment_period_id ?? "",
        },
      });
      if (tagGroupsData.error) throw new Error(tagGroupsData.error.message);
      const tagGroups = tagGroupsData.data.tag_group;

      toast({
        description:
          "You'll receive an email once ready, you can close this window or navigate elsewhere if needed.",
        duration: null,
        id: toastId,
        status: "loading",
        title: "Export in progress",
        isClosable: true,
      });

      const searchParamsObject = Object.fromEntries(searchParams.entries());
      if (dropoffFormTemplateId) {
        // Replace dropoffFormTemplateId in searchParams with the value of the prop dropoffFormTemplateId,
        // which is needed for the search query in the backend.
        // (The existing value is the same as formTemplate.id.)
        searchParamsObject.dropoffFormTemplateId = dropoffFormTemplateId;
      }
      const searchParamsString = new URLSearchParams(
        searchParamsObject
      ).toString();

      const selectionObject = await getSelectionObject();

      const verificationIds = formTemplate?.form_verifications?.map(
        (verification) => verification.id
      );
      const selectedColumnsExportKeys = getExportColumnKeysFromSearchParams(
        searchParams,
        verificationIds
      );
      const nonQuestionColumnKeys =
        savedViewCustomColumnsEnabled && selectedColumnsExportKeys
          ? selectedColumnsExportKeys.filter((key) => !isQuestionColumnKey(key))
          : getAllDefaultNonQuestionColumnKeys({
              isLotteryEnabled: formTemplate?.lottery_offers_enabled ?? false,
              isRankingEnabled: rankingEnabled ?? false,
              orgPath: organization.toNullable()?.path,
              isCurrentApplyingSchoolsEnabled:
                flags["current-applying-schools"].enabled,
              formVerifications: formTemplate?.form_verifications ?? [],
              tagGroups: tagGroups.map((tg) => ({
                id: tg.id,
                label: tg.name,
              })),
            });

      const selectedQuestionIds =
        savedViewCustomColumnsEnabled && selectedColumnsExportKeys
          ? questionIdsInOrder.filter((id) =>
              selectedColumnsExportKeys.includes(`question-${id}`)
            )
          : questionIdsInOrder;

      const formTemplateId = formTemplate?.id ?? "";
      const organizationId = organization.toNullable()?.id;
      if (user.status !== Status.OK && user.status !== Status.NOT_VERIFIED) {
        throw new Error("Can't find current user");
      }
      const login = user.data.loginType;
      if (login.type !== "email") {
        throw new Error("User does not have email");
      }
      const email = login.email;
      const postData: ExportRequest = {
        exportConfig: {
          searchParamsString,
          selection: selectionObject,
          formTemplateId: formTemplateId,
          selectedQuestionIds,
          selectedColumns: nonQuestionColumnKeys,
          questionKeysAsHeaders: true,
          exportSelectAnswerValues: false,
        },
        email,
      };
      const response = await api.createExport(organizationId ?? "", postData);
      if (!response?.pk) {
        throw new Error("Bulk export management API error");
      }
      const { pk } = response;

      // Poll for completion and auto-download
      const checkExportCompletion = async () => {
        try {
          const response = await api.getOneExport(organizationId ?? "", pk);
          if (!response || response.status === "FAILED") {
            throw new Error("Export failed");
          }
          if (response.status === "COMPLETED") {
            if (!response.url) {
              throw new Error("Export failed");
            }
            clearInterval(pollingInterval);
            triggerDownloadFromUrl(response.url);
            amplitude.track("Completed bulk export", {
              count: selection.size,
            });
            toast.update(toastId, {
              description:
                "It has been automatically downloaded and sent to your email.",
              status: "success",
              title: "Export ready",
              isClosable: true,
            });
          }
        } catch (error) {
          clearInterval(pollingInterval);
          errorHandler(error);
        }
      };
      const pollingInterval = setInterval(
        async () => await checkExportCompletion(),
        5000
      );
    } catch (error) {
      errorHandler(error);
    }
  };

  return (
    <>
      <Button
        leftIcon={<Icon as={DownloadIcon} />}
        onClick={() => {
          amplitude.track("Clicked bulk export", {
            export_quantity: selection.size,
          });
          onOpen();
        }}
        size="sm"
        variant="banner"
      >
        Export
      </Button>

      {/* Two separate dialogs
              - One for before showing the export customization form, allow choosing between "Express" and "Custom"
              - One for after submitting the export customization form, confirming submission
            This is needed since sharing the dialog for both purposes leads to complex state management.
            The modal must be closed before navigating to the form page; without closing it, the form page scroll is stuck. */}
      <BulkExportDialog
        isOpen={isOpen}
        onClose={onClose}
        selection={selection}
        handleBulkExport={handleBulkExport}
        formTemplate={formTemplate}
        dropoffFormTemplateId={dropoffFormTemplateId}
        getSelectionObject={getSelectionObject}
      />
      <BulkExportDialog
        isOpen={submittedIsOpen}
        onClose={submittedOnClose}
        selection={selection}
        handleBulkExport={handleBulkExport}
        formTemplate={formTemplate}
        exportSubmitted={{ text: exportSubmittedText, scheduled }}
      />
    </>
  );
};
